import React from 'react';
import { Check, X, Crown, Zap, Star, Gift } from 'lucide-react';
import { useInView } from 'react-intersection-observer';

const FreeSection = () => {
  const freeFeatures = [
    'Contas e cartões ilimitados',
    'Todas as funcionalidades essenciais desbloqueadas',
    'Previsão de saldo mensal',
    'Controle de gastos e metas financeiras',
    'Controle de faturas',
    'Relatórios completos',
    'Seus dados sempre protegidos na nuvem',
    'Suporte rápido direto pelo app',
    'Novidades e melhorias sempre que abrir o app',
    'Interface simples e sem limitações',
  ];

  const competitors = [
    {
      name: 'Mobills',
      plan: 'Plano Free',
      price: 'R$ 0,00',
      features: [
        { text: 'Registro de receitas e despesas limitado', available: true },
        { text: 'Cadastro de contas e cartões limitado', available: true },
        { text: 'Sincronização automática limitada', available: true },
        { text: 'Criação de metas limitada', available: true },
        { text: 'Gráficos e relatórios limitados', available: true },
        { text: 'Exportação de dados', available: false },
        { text: 'Organização com tags personalizada', available: false },
      ],
    },
    {
      name: 'Organizze',
      plan: 'Plano Pago',
      price: 'R$ 35,00/mês',
      features: [
        { text: 'Contas e cartões ilimitados', available: true },
        { text: 'Controle de gastos', available: true },
        { text: 'Controle de faturas', available: true },
        { text: 'Relatórios completos', available: true },
        { text: 'Backup na nuvem', available: true },
        { text: 'Criação de metas financeiras', available: false },
        { text: 'Não oferece plano gratuito', available: false },
      ],
    },
  ];

  return (
    <section id='gratuito' className='py-40 bg-white'>
      <div className='container mx-auto px-4'>
        {/* Header */}
        <div className='text-center mb-16'>
          <div className='inline-flex items-center bg-emerald-100 text-emerald-600 px-6 py-3 rounded-full font-semibold mb-6 animate-fade-slide-up'>
            <Gift className='w-5 h-5 mr-2' />
            FinZiin - Gratuito para Sempre
          </div>

          <h2 className='text-4xl lg:text-5xl font-bold mb-6 animate-fade-slide-up'>
            Por que o FinZiin é
            <span className='gradient-text block'>completamente gratuito?</span>
          </h2>

          <p className='text-xl text-gray-600 max-w-3xl mx-auto animate-fade-slide-up'>
            Acreditamos que o controle financeiro deve ser acessível a todos.
            Por isso, o <strong>FinZiin - Gratuito para Sempre</strong> oferece
            todas as funcionalidades essenciais, sem limites de contas ou
            cartões, com apenas 1 anúncio por vez, sem interferir na sua
            experiência.
          </p>
        </div>

        {/* Main Free Section */}
        <div className='bg-gradient-to-br from-emerald-50 to-blue-50 rounded-3xl p-8 lg:p-12 mb-16'>
          <div className='grid lg:grid-cols-2 gap-12 items-center'>
            {/* Left - FinZiin Features */}
            <div>
              <div className='flex items-center mb-6 animate-fade-slide-up'>
                <div className='bg-emerald-500 text-white p-3 rounded-xl mr-4'>
                  <Crown className='w-8 h-8' />
                </div>
                <div>
                  <h3 className='text-3xl font-bold text-gray-800'>
                    FinZiin - Gratuito
                  </h3>
                  <p className='text-emerald-600 font-semibold text-xl'>
                    R$ 0,00 para sempre
                  </p>
                </div>
              </div>

              <div className='space-y-4'>
                {freeFeatures.map((feature, index) => {
                  const { ref, inView } = useInView({
                    threshold: 0.2,
                    triggerOnce: true,
                  });
                  return (
                    <div
                      key={index}
                      ref={ref}
                      className={`flex items-center space-x-3 transition-all duration-500 transform ${
                        inView
                          ? 'opacity-100 translate-x-0'
                          : 'opacity-0 -translate-x-10'
                      }`}
                      style={{ transitionDelay: `${index * 0.1}s` }}
                    >
                      <Check className='w-5 h-5 text-emerald-500 flex-shrink-0' />
                      <span className='text-gray-700'>{feature}</span>
                    </div>
                  );
                })}
              </div>

              <div className='mt-8 p-4 bg-emerald-100 rounded-xl animate-fade-slide-up'>
                <div className='flex items-center space-x-2 mb-2'>
                  <Zap className='w-5 h-5 text-emerald-600' />
                  <span className='font-semibold text-emerald-800'>
                    Modelo Sustentável
                  </span>
                </div>
                <p className='text-emerald-700 text-sm'>
                  Apenas 1 anúncio por vez. Nada de interferirmos na sua
                  experiência.
                </p>
              </div>
            </div>

            {/* Right - Visual */}
            <div className='text-center animate-fade-slide-up'>
              <div className='bg-white rounded-2xl p-8 shadow-xl'>
                <div className='text-6xl mb-4'>🎉</div>
                <h4 className='text-2xl font-bold text-gray-800 mb-4'>
                  Economia Garantida
                </h4>
                <div className='text-4xl font-bold gradient-text mb-2'>
                  R$ 199,90
                </div>
                <p className='text-gray-600 mb-6'>
                  Valor que você economiza por ano comparado aos concorrentes
                </p>

                <div className='grid grid-cols-3 gap-4 text-sm'>
                  <div>
                    <div className='font-bold text-gray-800'>Outros Apps</div>
                    <div className='text-red-500'>R$ 199,90/ano</div>
                  </div>
                  <div>
                    <div className='text-gray-400'>vs</div>
                  </div>
                  <div>
                    <div className='font-bold text-gray-800'>
                      FinZiin - Gratuito
                    </div>
                    <div className='text-emerald-500 font-bold'>
                      R$ 0,00/ano
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className='mb-16 animate-fade-slide-up'>
          <h3 className='text-3xl font-bold text-center mb-4 text-gray-800'>
            Veja como o{' '}
            <span className='gradient-text'>FinZiin - Gratuito</span> se destaca
          </h3>
          <p className='text-center text-gray-600 max-w-2xl mx-auto mb-12'>
            Todas as funcionalidades essenciais desbloqueadas, sem pagar nada.
            Compare com outros apps.
          </p>

          {/* Nota sobre concorrentes */}
          <p className='text-sm text-gray-500 text-center mb-6'>
            Outros apps cobram mensalidade para recursos semelhantes.
          </p>

          <div className='grid lg:grid-cols-3 gap-6'>
            {/* FinZiin Column */}
            <div className='bg-gradient-to-b from-emerald-500 to-blue-500 text-white rounded-2xl p-8 relative transform scale-105 shadow-2xl animate-fade-slide-up'>
              <div className='absolute -top-4 left-1/2 transform -translate-x-1/2'>
                <div className='bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full text-sm font-bold'>
                  RECOMENDADO
                </div>
              </div>

              <div className='text-center mb-8'>
                <h4 className='text-2xl font-bold mb-2'>FinZiin - Gratuito</h4>
                <div className='text-3xl font-bold mb-2'>R$ 0,00</div>
                <p className='text-blue-100'>Plano essencial para sempre</p>
              </div>

              <div className='space-y-3 mb-8'>
                {freeFeatures.slice(0, 7).map((feature, index) => (
                  <div
                    key={index}
                    className='flex items-center space-x-3 transition-all duration-500 transform animate-fade-slide-up'
                    style={{ transitionDelay: `${index * 0.1}s` }}
                  >
                    <Check className='w-5 h-5 text-green-300 flex-shrink-0' />
                    <span className='text-sm'>{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href='https://play.google.com/store/apps/details?id=br.android.finziin'
                target='_blank'
                rel='noopener noreferrer'
                className='w-full bg-white text-blue-600 font-bold py-3 px-6 rounded-xl hover:bg-gray-100 transition-colors duration-300 text-center block'
              >
                Baixar Grátis
              </a>
            </div>

            {/* Competitors */}
            {competitors.map((competitor, index) => (
              <div
                key={index}
                className='bg-white border-2 border-gray-200 rounded-2xl p-8 animate-fade-slide-up'
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                <div className='text-center mb-8'>
                  <h4 className='text-xl font-bold mb-2 text-gray-800'>
                    {competitor.name}
                  </h4>
                  <div className='text-2xl font-bold mb-2 text-gray-800'>
                    {competitor.price}
                  </div>
                </div>

                <div className='space-y-3 mb-8'>
                  {competitor.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className='flex items-center space-x-3 transition-all duration-500 transform animate-fade-slide-up'
                      style={{ transitionDelay: `${featureIndex * 0.05}s` }}
                    >
                      {feature.available ? (
                        <Check className='w-5 h-5 text-green-500 flex-shrink-0' />
                      ) : (
                        <X className='w-5 h-5 text-red-500 flex-shrink-0' />
                      )}
                      <span
                        className={`text-sm ${
                          feature.available ? 'text-gray-700' : 'text-gray-400'
                        }`}
                      >
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <button className='w-full bg-gray-200 text-gray-500 font-bold py-3 px-6 rounded-xl cursor-default'>
                  {competitor.plan}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className='text-center animate-fade-slide-up'>
          <div className='bg-gray-900 text-white rounded-2xl p-8 max-w-4xl mx-auto'>
            <h3 className='text-3xl font-bold mb-4'>
              Comece agora com o FinZiin - Gratuito para Sempre
            </h3>
            <p className='text-gray-300 mb-6 text-lg'>
              Todas as funcionalidades essenciais desbloqueadas, sem pagar nada.
            </p>
            <a
              href='https://play.google.com/store/apps/details?id=br.android.finziin'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center btn-primary bg-emerald-500 hover:bg-emerald-600 transition-all py-4 px-8 rounded-xl font-bold shadow-lg'
            >
              <Star className='w-5 h-5 mr-2' />
              Baixar o FinZiin Grátis
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FreeSection;
